import React, { useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip, CircularProgress } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import FlagIcon from "@mui/icons-material/Flag";
import moment from "moment";
import BuscarTramosFilterPT from "./buscarTramosFilterPT";
import { StyledTraslados } from "../../../../../styled/traslados";
import { tableConfig } from "../../../../../commons/table/config/tableConfig";
import { FileUpload } from "@mui/icons-material";

const BuscarTramosTablePT = ({
  filters,
  onSearch,
  onClear,
  results,
  loading,
  onVisualizarTraslado,
  onCargarObservaciones,
  onRechazarTraslado,
  onMarcarPosibleNegativo,
  onExportar,
  isExporting
}) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "nroTraslado",
        header: "Nro traslado",
        size: 80,
      },
      {
        accessorKey: "idTramo",
        header: "Nro tramo",
        size: 80,
        Cell: ({ cell }) => <b>{cell.getValue() || ""}</b>,
      },
      {
        accessorKey: "nroOrden",
        header: "Tramo",
        size: 80,
      },
      {
        id: "sentido",
        header: "Sentido",
        size: 80,
        Cell: ({ row }) => {
          const isVuelta = row.original.esRetorno;
          return (
            <Box
              component="span"
              sx={{
                backgroundColor: isVuelta ? "#ffe9cc" : "#e3edfa",
                color: isVuelta ? "#8a5000" : "#2b5f96",
                borderRadius: "10px",
                padding: "2px 9px",
                fontSize: "11px",
                display: "inline-block"
              }}
            >
              {isVuelta ? "Vuelta" : "Ida"}
            </Box>
          );
        }
      },
      {
        accessorKey: "trabajador",
        header: "Trabajador",
      },
      {
        accessorKey: "siniestro",
        header: "Siniestro",
      },
      {
        accessorKey: "calleOrigen",
        header: "Origen",
      },
      {
        accessorKey: "provinciaOrigen",
        header: "Prov.",
      },
      {
        accessorKey: "localidadOrigen",
        header: "Localidad",
      },
      {
        accessorKey: "calleDestino",
        header: "Destino",
      },
      {
        accessorKey: "provinciaDestino",
        header: "Prov.",
      },
      {
        accessorKey: "localidadDestino",
        header: "Localidad",
      },
      {
        id: "fecha",
        header: "Fecha",
        Cell: ({ row }) => moment(row.original.fechaHoraTraslado).format("DD/MM/YYYY"),
      },
      {
        id: "hora",
        header: "Hora",
        Cell: ({ row }) => moment(row.original.fechaHoraTraslado).format("HH:mm"),
      },
      {
        accessorKey: "gestor",
        header: "Gestor",
      },
      {
        accessorKey: "agenciaNombre",
        header: "Agencia",
      },
      {
        accessorKey: "estadoTramoNombre",
        header: "Estado del tramo",
        Cell: ({ row }) => {
          const estado = row.original.estadoTramoNombre;
          let color = "#9aa3ab";
          switch (estado) {
            case "Pendiente de validación datos": color = "#5b9bf5"; break;
            case "Pendiente Autorización": color = "#7e57c2"; break;
            case "Autorizado": color = "#66bb6a"; break;
            case "Despachado": color = "#2e7d32"; break;
            case "Rechazado por Agencia": color = "#e64a19"; break;
            case "Negativo": color = "#f9a825"; break;
            case "Rechazado": color = "#d94040"; break;
            case "Cancelado": color = "#78909c"; break;
            case "Cancelado con penalidad": color = "#8d6e63"; break;
            case "Cerrado": color = "#546e7a"; break;
            case "Cerrado Automáticamente": color = "#90a4ae"; break;
            default: break;
          }

          return (
            <Box
              component="span"
              sx={{
                backgroundColor: color,
                color: "#fff",
                borderRadius: "10px",
                padding: "3px 9px",
                fontSize: "10.5px",
                fontWeight: 500,
                display: "inline-block",
                whiteSpace: "nowrap"
              }}
            >
              {estado}
            </Box>
          )
        }
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: results,
    state: { isLoading: loading },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    renderTopToolbarCustomActions: () => (
      <Tooltip title="Exportar">
        <span>
          <IconButton
            color="primary"
            onClick={onExportar}
            disabled={loading || !results || results.length === 0 || isExporting}
          >
            {isExporting ? <CircularProgress size={24} color="inherit" /> : <FileUpload />}
          </IconButton>
        </span>
      </Tooltip>
    ),
    renderRowActions: ({ row }) => {
      const isDespachado = row.original.estadoTramoNombre === "Despachado";
      return (
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
          <Tooltip title="Visualizar traslado (solo lectura)">
            <IconButton size="small" onClick={() => onVisualizarTraslado(row.original)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cargar observaciones">
            <IconButton size="small" color="primary" onClick={() => onCargarObservaciones(row.original)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isDespachado ? "Rechazar traslado" : "Sólo se puede rechazar un traslado Despachado"}>
            <span>
              <IconButton size="small" color="error" disabled={!isDespachado} onClick={() => onRechazarTraslado(row.original)}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isDespachado ? "Marcar Posible Negativo" : "Sólo sobre un traslado Despachado"}>
            <span>
              <IconButton size="small" color="warning" disabled={!isDespachado} onClick={() => onMarcarPosibleNegativo(row.original)}>
                <FlagIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original.esRetorno ? '#fffdf6' : 'inherit',
      },
    }),
  });

  return (
    <Box p={2}>
      <StyledTraslados.Title variant="h6" gutterBottom>
        Buscar Traslados
      </StyledTraslados.Title>

      <BuscarTramosFilterPT initialValues={filters} onSearch={onSearch} onClear={onClear} />

      <MaterialReactTable table={table} />
    </Box>
  );
};

export default BuscarTramosTablePT;